// src/pages/plan/ProgressManagement.jsx
import React, { useMemo } from "react";
import { BarChart3, Building2, CheckCircle, AlertCircle, Clock, FileCheck } from "lucide-react";
import { clsx } from "clsx";
import {useProgressPage} from "@hooks";
import { PageHeader, FilterBar, DataTable } from "@components";
import { ProgressDetailModal } from "./ProgressDetailModal";

export function ProgressManagement() {
  const {
    filters, setFilters, page, onSearch,
    selectedBiz, setSelectedBiz, detailList, detailLoading
  } = useProgressPage();

  const handleReset = () => {
    setFilters({
      assignee: '', 
      companyName: '', 
      status: '', 
      searchYear: new Date().getFullYear().toString(), 
      pageNum: 1 // 페이지도 1페이지로 리셋
    });
  };

  const helpText = `사업장별 방문 및 컨설팅 산출물 등록 진행 현황을 한눈에 파악하는 화면입니다.
목록을 클릭하면 회차별 상세 진행 타임라인을 확인할 수 있습니다.`;

// 🌟 (추가) 현재 페이지 데이터를 기반으로 KPI 통계 계산
  const kpiStats = useMemo(() => {
    const rows = page.rows || [];
    let totalAssigned = rows.length;
    let totalRounds = 0;
    let completedRounds = 0;
    let uploadedRounds = 0;
    let warningCount = 0;

    rows.forEach(r => {
      totalRounds += r.totalRounds;
      completedRounds += r.completedRounds;
      uploadedRounds += r.uploadedRounds;
      // 🌟 수정: 서류 미등록이거나 일정 지연(delayedRounds > 0)일 때 경고 카운트 증가
      if (r.completedRounds > r.uploadedRounds || r.delayedRounds > 0) warningCount++;
    });

    return {
      totalBiz: page.total || 0,
      visitRate: totalRounds === 0 ? 0 : Math.round((completedRounds / totalRounds) * 100),
      uploadRate: completedRounds === 0 ? 0 : Math.round((uploadedRounds / completedRounds) * 100),
      warningCount
    };
  }, [page]);

  // 진행 상태 뱃지 렌더링 함수
  const renderStatusBadge = (statusLabel) => {
    switch (statusLabel) {
      case '완료':
        return <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20"><CheckCircle className="h-3 w-3"/> 완료</span>;
      case '서류 미등록':
        return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/10"><AlertCircle className="h-3 w-3"/> 서류 미등록</span>;
        // 🌟 추가: 일정 지연 뱃지 (주황색 계열)
      case '일정 지연': 
        return <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-1 text-xs font-semibold text-orange-700 ring-1 ring-inset ring-orange-600/20"><AlertCircle className="h-3 w-3"/> 일정 지연</span>;
      default:
        return <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10"><Clock className="h-3 w-3"/> 진행중</span>;
    }
  };

  const columns = [
    { label: "사업장명" },
    { label: "관할지사", align: "text-center" },
    { label: "담당자", align: "text-center" },
    { label: "총 배정/완료", align: "text-center" },
    { label: "전체 진행률 (서류 등록 기준)", align: "text-center" },
    { label: "최근 방문(예정)일", align: "text-center" },
    { label: "진행 상태", align: "text-center" }
  ];

  // 안전한 배열 매핑을 위한 처리
  const safeDetailList = Array.isArray(detailList) ? detailList : [];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <PageHeader 
        icon={BarChart3} iconBgColor="bg-indigo-600"
        title="컨설팅 진행 현황" description="사업장별 방문 및 산출물 진행 상태를 모니터링합니다." 
        helpId="progress" initialHelpText={helpText}
      />

      {/* 🌟 1. KPI Summary Cards 영역 추가 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center shrink-0"><Building2 className="w-6 h-6 text-blue-600" /></div>
          <div><p className="text-sm font-medium text-slate-500 mb-0.5">전체 관리 사업장</p><p className="text-2xl font-bold text-slate-800">{kpiStats.totalBiz} <span className="text-base font-medium text-slate-500">건</span></p></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center shrink-0"><Clock className="w-6 h-6 text-indigo-600" /></div>
          <div className="flex-1"><p className="text-sm font-medium text-slate-500 mb-1">평균 방문 실시율</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${kpiStats.visitRate}%` }}></div></div>
              <span className="text-lg font-bold text-slate-800">{kpiStats.visitRate}%</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center shrink-0"><FileCheck className="w-6 h-6 text-green-600" /></div>
          <div className="flex-1"><p className="text-sm font-medium text-slate-500 mb-1">산출물 등록률</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${kpiStats.uploadRate}%` }}></div></div>
              <span className="text-lg font-bold text-slate-800">{kpiStats.uploadRate}%</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center shrink-0"><AlertCircle className="w-6 h-6 text-red-600" /></div>
          <div><p className="text-sm font-medium text-slate-500 mb-0.5">서류 누락/경고</p><p className="text-2xl font-bold text-red-600">{kpiStats.warningCount} <span className="text-base font-medium text-red-400">건</span></p></div>
        </div>
      </div>

      <FilterBar onReset={handleReset}>
         {/* 검색 연도 필터 */}
         <div className="space-y-1.5 w-32">
          <label className="text-xs font-semibold text-slate-500">조회 연도</label>
          <select className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            value={filters.searchYear} onChange={e => setFilters(p => ({...p, searchYear: e.target.value}))}>
            <option value="2026">2026년</option>
            <option value="2025">2025년</option>
          </select>
        </div>
        {/* 🌟 추가: 진행 상태 필터 */}
        <div className="space-y-1.5 w-40">
          <label className="text-xs font-semibold text-slate-500">진행 상태</label>
          <select className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            value={filters.status} onChange={e => setFilters(p => ({...p, status: e.target.value}))}>
            <option value="">전체 상태</option>
            <option value="IN_PROGRESS">진행중</option>
            <option value="COMPLETED">완료</option>
            <option value="MISSING">서류 미등록</option>
            <option value="DELAYED">일정 지연</option>
          </select>
        </div>
        <div className="space-y-1.5 flex-1 max-w-[250px]">
          <label className="text-xs font-semibold text-slate-500">사업장명</label>
          <input type="text" placeholder="회사명 검색" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" value={filters.companyName} onChange={e => setFilters(p => ({...p, companyName: e.target.value}))} onKeyDown={e => e.key === 'Enter' && onSearch()} />
        </div>
        <div className="space-y-1.5 flex-1 max-w-[250px]">
          <label className="text-xs font-semibold text-slate-500">담당자</label>
          <input type="text" placeholder="담당자 검색" className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" value={filters.assignee} onChange={e => setFilters(p => ({...p, assignee: e.target.value}))} onKeyDown={e => e.key === 'Enter' && onSearch()} />
        </div>
      </FilterBar>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <DataTable 
          columns={columns} page={page} itemName="사업장"
          onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
          renderRow={(row) => (
            <tr key={`${row.bizMgtNo}-${row.staffId}`} onClick={() => setSelectedBiz(row)} className="cursor-pointer hover:bg-indigo-50/50 transition-colors">
              <td className="px-6 py-4 font-semibold text-slate-800">{row.companyName}</td>
              <td className="px-6 py-4 text-center text-slate-600">{row.branchName}</td>
              <td className="px-6 py-4 text-center text-slate-600">{row.staffNm}</td>
              <td className="px-6 py-4 text-center font-medium">
                 <span className="text-indigo-600">{row.completedRounds}</span> / <span className="text-slate-400">{row.totalRounds}</span>
              </td>
              <td className="px-6 py-4">
                 {/* 진행률 프로그레스 바 구현 */}
                 <div className="flex items-center gap-3">
                   <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                     {/* 🌟 2. 수정: style 속성 내의 백틱(`)과 달러($) 기호 정상화 */}
                     <div className={clsx("h-full rounded-full transition-all duration-500", row.progressRate === 100 ? "bg-green-500" : "bg-indigo-500")} style={{ width: `${row.progressRate}%` }}></div>
                   </div>
                   <span className="text-xs font-bold text-slate-600 w-8">{row.progressRate}%</span>
                 </div>
              </td>
              <td className="px-6 py-4 text-center text-slate-500">{row.lastVisitDate ? row.lastVisitDate.substring(0,10) : '-'}</td>
              <td className="px-6 py-4 text-center">{renderStatusBadge(row.statusLabel)}</td>
            </tr>
          )}
        />
      </div>

      <ProgressDetailModal 
        selectedBiz={selectedBiz} 
        onClose={() => setSelectedBiz(null)} 
        detailList={detailList} 
        detailLoading={detailLoading} 
      />
      
    </div>
  );
}